<template>
	<div class="favouriteStore">
		<div class="font-body-s-bold flex items-center justify-between bg-level-01 py-02 pl-03 pr-03">
			<div class="nameAndCount flex items-center gap-02 overflow-x-hidden text-ellipsis whitespace-nowrap">
				<FavouriteButton
					class="mr-02"
					:value="areAllSKUsMarkedAsFavourite(store.id) ? 'added' : areSomeSKUsMarkedAsFavourite(store.id) ? 'partially-added' : 'removed'"
					@add="addAllSKUsToFavourites(store.id)"
					@remove="removeAllSKUsFromFavourites(store.id)"
				/>

				<TooltipIfEllipsis :tooltip-text="store.displayName">
					{{ store.displayName }}
				</TooltipIfEllipsis>

				<span>({{ numberOfFavouriteSKUs }} {{ t('favouriteSKUsPanel.of') }} {{ numberOfAllSKUs }})</span>
			</div>

			<IconButton
				icon="chevron-up"
				:class="['expandIcon ml-05 cursor-pointer', { expandIcon__down: !isExpanded }]"
				@click="toggleExpandedState()"
			/>
		</div>

		<div v-if="isExpanded" class="products mt-03 flex flex-col gap-02">
			<FavouriteProduct v-for="[product, skusForProduct] in products" :key="product" :name="product" :skus="skusForProduct" :store="store" />
		</div>
	</div>
</template>

<script setup lang="ts">
import { IconButton, TooltipIfEllipsis, useTranslation } from '@bluebrick/saas-ui'
import { groupBy } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { computed, ref, toRefs } from 'vue'
import { sortByHumanNameComparator, sortByProductNameComparator } from '@/common/utils'
import FavouriteButton from '@/features/skus/components/FavouriteButton.vue'
import FavouriteProduct from '@/features/skus/components/FavouriteProduct.vue'
import { useSkusStore } from '@/features/skus/useSkusStore'
import { type Store } from '@/types'

const props = defineProps<{
	store: Store
	filterString: string
}>()

const { store, filterString } = toRefs(props)

const { t } = useTranslation()
const { skus, temporaryFavouriteSKUIds } = storeToRefs(useSkusStore())
const { addAllSKUsToFavourites, removeAllSKUsFromFavourites, areAllSKUsMarkedAsFavourite, areSomeSKUsMarkedAsFavourite } = useSkusStore()

const isExpanded = ref(true)

const skusForStore = computed(() =>
	(skus.value?.[store.value.id] ?? [])
		.filter(({ human_name }) => human_name.toLowerCase().includes(filterString.value.trim().toLowerCase()))
		.sort(sortByProductNameComparator)
)

const products = computed(() => {
	const groupedByProduct = Object.entries(groupBy(skusForStore.value, 'product_name'))

	groupedByProduct.forEach(([_, skusForProduct]) => skusForProduct.sort(sortByHumanNameComparator))

	return groupedByProduct
})

const numberOfAllSKUs = computed(() => skusForStore.value.length)
const numberOfFavouriteSKUs = computed(() =>
	skusForStore.value.reduce((sum, sku) => {
		if (temporaryFavouriteSKUIds.value.has(sku.unique_sku_id)) {
			return sum + 1
		}

		return sum
	}, 0)
)

function toggleExpandedState() {
	isExpanded.value = !isExpanded.value
}
</script>

<style lang="scss" scoped>
.favouriteStore {
	.expandIcon {
		transition: all 0.1s linear;

		&__down {
			transform: scaleY(-1);
		}
	}

	&:not(:first-of-type) {
		padding-top: theme('spacing.03');
	}
}
</style>
